/**
 * Bounded rest-prop runtime allow-list filter for primitive
 * pass-through props.
 *
 * BACKGROUND (codex Card r1 + SectionHeader r1 + this v5 audit batch):
 * The kit's primitives opt for a NARROW rest-prop surface — only
 * `id` / `title` / `lang` / `dir` / `data-*` / `aria-*` pass through to
 * the DOM. The TypeScript surface uses
 *
 *     type AllowedRest = Pick<React.HTMLAttributes<…>, 'id'|'title'|…>
 *       & { [key: `data-${string}`]: …; [key: `aria-${string}`]: …; };
 *
 * which prevents adopters from passing `tabIndex`, `role`, etc. at
 * the call site. But a typed-bag spread (`<Primitive {...(bag as any)}>`
 * or plain JS) can still inject reserved keys at runtime. `pickAllowed`
 * filters the spread on the way out so the primitive's controlled
 * a11y / focus / interaction contract holds regardless of caller
 * discipline.
 *
 * USAGE PATTERN (after future migration; 10 primitives currently
 * inline-duplicate this 7-line function):
 *
 *     import { pickAllowed } from '../internal/pickAllowed';
 *     // ...
 *     const restProps = pickAllowed(rest as Record<string, unknown>) as
 *       React.HTMLAttributes<HTMLDivElement>;
 *
 * The shared util ships in v5.0 as a NEW MODULE; the existing
 * primitives keep their inline duplicates for this release to avoid
 * conflicting with the 10 in-flight PRs. A follow-up consolidation
 * PR (post-v5 cut) will migrate each primitive to import from here.
 */

/** Literal keys allowed in the default allow-list. Primitives that
 *  need a different literal set (e.g., Form needs the native form
 *  attrs) pass their own keys via the `extraLiteralKeys` parameter. */
const DEFAULT_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);

/**
 * Filter a rest-prop object down to allow-listed keys.
 *
 * Always allows: `data-${string}` and `aria-${string}` (template
 * literal types at the TS level; prefix string-startsWith at runtime).
 *
 * @param rest        — the raw rest-prop object (from a function
 *                      destructure or a spread)
 * @param extraLiteralKeys — optional set of additional literal keys
 *                      to allow (e.g., `['action', 'method']` for a
 *                      Form wrapper). Defaults to the standard
 *                      {id, title, lang, dir} set.
 */
export function pickAllowed(
  rest: Record<string, unknown>,
  extraLiteralKeys?: ReadonlySet<string>,
): Record<string, unknown> {
  const allowed = extraLiteralKeys ?? DEFAULT_LITERAL_KEYS;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (allowed.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

/** Standard literal-keys set (re-exported so primitives building
 *  their own extraLiteralKeys can compose on top of the default). */
export const STANDARD_LITERAL_KEYS = DEFAULT_LITERAL_KEYS;
